import { FC, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';
import { Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { endOfDay, format, startOfDay } from 'date-fns';
import { downloadExportErrorsReport, getExportErrors } from 'entities/Logs/logsApi';
import { ExportErrorsReportReq } from 'entities/Logs/logsTypes';
import { PageWrapper } from 'pages/_layouts/PageWrapper';
import { ColorPalette } from 'shared/consts/colorPalette';
import { downloadFileOnPC } from 'shared/helpers/downloadFile';
import { useCustomSearchParams } from 'shared/hooks/use-custom-search';
import { showErrorNotification } from 'shared/helpers/showErrorNotification';
import { Button } from 'shared/ui/Button';
import { EmptyTable } from 'shared/ui/EmptyTable';
import { Input } from 'shared/ui/Input';
import { Paper } from 'shared/ui/Paper';
import { TableWithHeader } from 'shared/ui/tables/TableWithHeader';
import { ExportErrorRow, ExportErrorsColumns } from './exportErrorsColumns';

type DateFilters = { from: Date | null; to: Date | null };

const EMPTY_DATES: DateFilters = { from: null, to: null };

export const ExportErrors: FC = () => {
	const { t } = useTranslation();
	const [dates, setDates] = useState<DateFilters>(EMPTY_DATES);
	const [filters, setFilters] = useState<ExportErrorsReportReq>({});
	const { searchQueries, changeSearchQueries, resetSearchQueries } = useCustomSearchParams();
	const [isDownloading, setIsDownloading] = useState(false);

	const { data } = useQuery({
		queryKey: ['logs/export-errors', filters, searchQueries],
		queryFn: () => getExportErrors({ ...filters, ...searchQueries }),
		keepPreviousData: true,
		onError: (err: AxiosError) => showErrorNotification(err),
	});

	const rows: ExportErrorRow[] = useMemo(
		() => data?.data.results.map((log) => ({ ...log, id: log._id })) ?? [],
		[data],
	);

	const handleApply = () => {
		setFilters({
			from: dates.from ? startOfDay(dates.from).toISOString() : undefined,
			to: dates.to ? endOfDay(dates.to).toISOString() : undefined,
		});
		changeSearchQueries({ page: 1 });
	};

	const handleReset = () => {
		setDates(EMPTY_DATES);
		setFilters({});
		resetSearchQueries();
	};

	const handleDownload = async () => {
		setIsDownloading(true);
		try {
			const response = await downloadExportErrorsReport(filters);
			downloadFileOnPC(response, `export-errors-${format(new Date(), 'yyyy-MM-dd_HH-mm')}.csv`);
		} catch (err) {
			showErrorNotification(err as AxiosError);
		} finally {
			setIsDownloading(false);
		}
	};

	const renderDatePicker = (field: keyof DateFilters) => (
		<DatePicker
			value={dates[field]}
			inputFormat={'dd/MM/yyyy'}
			onChange={(value: Date | null) => setDates((prev) => ({ ...prev, [field]: value }))}
			renderInput={({ inputRef, inputProps, InputProps }) => (
				<Input
					name={`export-errors-${field}`}
					label={t(`integrations.export_errors.${field}`)}
					inputRef={inputRef}
					inputProps={inputProps}
					endAdornment={InputProps?.endAdornment}
					sx={{ width: '250px', marginBottom: 0 }}
				/>
			)}
		/>
	);

	return (
		<PageWrapper spacing="xLg">
			<Typography variant={'h4'} color={ColorPalette.astronaut}>
				{t('integrations.export_errors.title')}
			</Typography>
			<Typography variant={'subtitle1'} color={ColorPalette.baliHai}>
				{t('integrations.export_errors.description')}
			</Typography>

			<Paper
				sx={{
					width: '100%',
					display: 'flex',
					alignItems: 'flex-end',
					gap: 2,
				}}
			>
				{renderDatePicker('from')}
				{renderDatePicker('to')}
				<Button variant={'outlined'} onClick={handleApply} size="small">
					{t('shared_texts.apply')}
				</Button>
				<Button variant={'text'} onClick={handleReset} size="small">
					{t('shared_texts.reset')}
				</Button>
				<Button
					variant={'contained'}
					disabled={isDownloading}
					onClick={handleDownload}
					size="small"
					sx={{ marginLeft: 'auto' }}
				>
					{t('integrations.export_errors.download_csv')}
				</Button>
			</Paper>

			<Typography variant={'h5'} color={ColorPalette.astronaut}>
				{t('integrations.export_errors.table_title')}
			</Typography>

			{rows.length ? (
				<TableWithHeader
					columns={ExportErrorsColumns}
					rows={rows}
					pagination={{
						page: searchQueries.page,
						totalCount: data?.data.totalCount || 0,
						perPage: data?.data.perPage || 0,
						onPageChange: (page) => changeSearchQueries({ page }),
					}}
				/>
			) : (
				<EmptyTable text={t('integrations.export_errors.empty')} />
			)}
		</PageWrapper>
	);
};
